import { watch, type WatchStopHandle } from 'vue'
import { useUserStore } from '@/stores/user'

/**
 * 全局监听用户 role 变化
 * 
 * @example
 * // 方式1: 在组件中使用
 * import { useRoleWatcher } from '@/composables/useRoleWatcher'
 * 
 * const stopWatcher = useRoleWatcher((newRole, oldRole) => {
 *   console.log('Role changed:', { oldRole, newRole })
 *   // 处理 role 变化逻辑
 * })
 * 
 * onUnmounted(() => {
 *   stopWatcher() // 清理监听
 * })
 * 
 * @example
 * // 方式2: 使用事件总线（允许多个地方监听）
 * import { roleWatcherManager } from '@/composables/useRoleWatcher'
 * 
 * const unsubscribe = roleWatcherManager.onRoleChange((newRole, oldRole) => {
 *   console.log('Role changed:', { oldRole, newRole })
 * })
 * 
 * onUnmounted(() => {
 *   unsubscribe() // 取消监听
 * })
 * 
 * @param callback - role 变化时的回调函数
 * @param immediate - 是否立即执行一次回调
 * @returns 停止监听的函数
 */
export function useRoleWatcher(
  callback: (newRole: number | undefined, oldRole: number | undefined) => void,
  immediate = false
): WatchStopHandle {
  const userStore = useUserStore()

  // 监听 userInfo.role 的变化
  const stopWatcher = watch(
    () => userStore.userInfo?.role,
    (newRole, oldRole) => {
      callback(newRole, oldRole)
    },
    {
      immediate,
      deep: false
    }
  )

  return stopWatcher
}

/**
 * 全局 role 变化事件总线
 * 允许多个地方监听 role 变化
 */
class RoleWatcherManager {
  private watchers: Set<(newRole: number | undefined, oldRole: number | undefined) => void> = new Set()
  private currentRole: number | undefined = undefined

  /**
   * 注册 role 变化监听器
   */
  onRoleChange(callback: (newRole: number | undefined, oldRole: number | undefined) => void) {
    this.watchers.add(callback)
    
    // 如果已有 role 值，立即执行一次
    if (this.currentRole !== undefined) {
      callback(this.currentRole, undefined)
    }

    // 返回取消监听的函数
    return () => {
      this.watchers.delete(callback)
    }
  }

  /**
   * 触发 role 变化事件
   */
  emitRoleChange(newRole: number | undefined, oldRole: number | undefined) {
    this.currentRole = newRole
    this.watchers.forEach(callback => {
      try {
        callback(newRole, oldRole)
      } catch (error) {
        console.error('Role watcher callback error:', error)
      }
    })
  }

  /**
   * 清除所有监听器
   */
  clear() {
    this.watchers.clear()
  }
}

// 导出单例
export const roleWatcherManager = new RoleWatcherManager()

